import React, { useEffect, useState } from 'react';
import {
    Modal,
    ModalOverlay,
    ModalContent,
    ModalHeader,
    ModalCloseButton,
    ModalBody,
    ModalFooter,
    Flex,
    Skeleton,
    Button,
    FormControl,
    Heading,
    Input,
    InputGroup,
    InputLeftElement,
    List,
    ListItem,
    Tag,
    TagLabel,
    TagCloseButton
} from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import { JButton, JGoogleIcon, JTypography } from '../../components';
import InfiniteScroll from 'react-infinite-scroll-component';
import { headingTheme } from '../../theme/components';
import { useDebounce } from 'use-debounce';
import { useQuery } from 'react-query';
import { apiGetMember } from '../../apis/memberGroupApi';

interface ModalApplyMemberGroupProps {
    isOpen: boolean;
    onClose: () => void;
    groupMember: any;
    groupMemberAccess: any;
    setGroupMemberAccess: (callback: (prev: any) => any) => void;
    updateGroupMemberAccess: { mutate: () => void; isLoading: boolean };
    groupMemberAccessDetail: { isLoading: boolean };
}

const ModalApplyMemberGroup = ({
    isOpen,
    onClose,
    groupMember,
    groupMemberAccess,
    setGroupMemberAccess,
    updateGroupMemberAccess,
    groupMemberAccessDetail,
}: ModalApplyMemberGroupProps) => {
    const { t } = useTranslation();

    const [formMember, setFormMember] = useState('');
    const [debouncedSearchTerm] = useDebounce(formMember, 500);
    const [allMembers, setAllMembers] = useState<any[]>([]);
    const [page, setPage] = useState(1);

    console.log(groupMemberAccess)

    const members = useQuery(
        ['member-group-modal-get-members', page, debouncedSearchTerm],
        () => handleGetMembers(page, debouncedSearchTerm),
        {
            keepPreviousData: true,
            enabled: !!debouncedSearchTerm || page > 1
        }
    );

    async function handleGetMembers(page: number, search: string) {
        return await apiGetMember({
            is_active: 1,
            show: 10,
            page,
            search
        });
    }

    useEffect(() => {
        setAllMembers([]);
        setPage(1);
    }, [debouncedSearchTerm]);

    useEffect(() => {
        if (Array.isArray(members?.data?.data)) {
            setAllMembers((prev) =>
                page === 1 ? members.data.data : [...prev, ...members.data.data]
            );
        }
    }, [members.data]);

    const handleClose = () => {
        setFormMember('');
        setAllMembers([]);
        setPage(1);
        onClose();
    };

    return (
        <Modal isOpen={isOpen} onClose={handleClose} size='xl'>
            <ModalOverlay />
            <ModalContent backgroundColor={'surfacePlus1'} padding={'24px'}>
                <ModalHeader p='0'>
                    <Heading size='chakra_title_large'>
                        {t('group_anggota_modal_berlakukan_judul')}
                    </Heading>
                </ModalHeader>
                <ModalCloseButton
                    top={'24px'}
                    right={'24px'}
                    isDisabled={updateGroupMemberAccess.isLoading}
                />
                <ModalBody
                    as={Flex}
                    flexDirection={'column'}
                    gap={'16px'}
                    padding={'0px'}
                    marginTop={'24px'}
                >
                    <Flex gap="3px">
                        <JTypography size={'label-medium'}>
                            {t('group_anggota_modal_berlakukan_beri_akss')}
                        </JTypography>
                        <JTypography size={'label-medium'} fontWeight="600">
                            {groupMember?.display_name}
                        </JTypography>
                    </Flex>
                    {groupMemberAccessDetail.isLoading ? (
                        <Skeleton height={'40px'} width={'100%'} />
                    ) : (
                        <FormControl id="text">
                            <InputGroup>
                                <InputLeftElement pointerEvents="none">
                                    <JGoogleIcon name="search" />
                                </InputLeftElement>
                                <Input
                                    autoComplete="off"
                                    placeholder={`Search ${t('group_anggota_modal_ubah_anggota')}`}
                                    type="text"
                                    value={formMember}
                                    onChange={(e: any) => setFormMember(e.target.value)}
                                    {...headingTheme?.sizes?.chakra_label_large}
                                />
                            </InputGroup>

                            {formMember && (
                                <InfiniteScroll
                                    dataLength={allMembers.length}
                                    next={() => setPage((prev) => prev + 1)}
                                    hasMore={
                                        (members?.data?.metadata?.current_page || 1) <
                                        (members?.data?.metadata?.last_page || 1)
                                    }
                                    loader={null}
                                    scrollableTarget="scrollableMemberList"
                                >
                                    <List
                                        id="scrollableMemberList"
                                        position="absolute"
                                        zIndex="10"
                                        bg="surfacePlus1"
                                        w="100%"
                                        borderRadius="16px"
                                        maxH="150px"
                                        overflow="auto"
                                    >
                                        {allMembers.map((m: any, i: number) => (
                                            <ListItem key={i}>
                                                <Button
                                                    p="12px"
                                                    w="100%"
                                                    variant="ghost"
                                                    borderRadius="16px"
                                                    onClick={() => {
                                                        setGroupMemberAccess((prev: any) => {
                                                            const newMember = {
                                                                value: m.id,
                                                                label: m.user.display_name,
                                                            };
                                                            const isExist = prev.access?.some(
                                                                (member: any) => member.value === newMember.value
                                                            );
                                                            if (isExist) return prev;
                                                            return {
                                                                ...prev,
                                                                access: [...(prev.access || []), newMember],
                                                            };
                                                        })
                                                        setFormMember('')
                                                    }}
                                                >
                                                    <Flex w="100%" gap="5px">
                                                        <Heading size="chakra_label_large">
                                                            {m.user.display_name}
                                                        </Heading>
                                                    </Flex>
                                                </Button>
                                            </ListItem>
                                        ))}
                                    </List>
                                </InfiniteScroll>
                            )}
                        </FormControl>
                    )}
                    {groupMemberAccess?.access.length > 0 &&
                        <JTypography size="label-medium">
                            {t('group_anggota_modal_ubah_anggota')}
                        </JTypography>
                    }
                    {groupMemberAccessDetail.isLoading ? (
                        <Skeleton height={'40px'} width={'100%'} />
                    ) : (
                        Array.isArray(groupMemberAccess?.access) && groupMemberAccess?.access.length > 0 && (
                            <Flex wrap="wrap" gap="8px" mt="6px">
                                {groupMemberAccess?.access.map((member: any, idx: number) => (
                                    <Tag
                                        key={member.id || idx}
                                        p='6px 8px'
                                        borderRadius="12px"
                                        bg='surfacePlus4'
                                        border='1px solid'
                                        borderColor='primary'
                                    >
                                        <TagLabel>
                                            <Heading size='chakra_label_large' color='primary'>
                                                {member.label}
                                            </Heading>
                                        </TagLabel>
                                        <TagCloseButton color='primary'
                                            onClick={() =>
                                                setGroupMemberAccess((prev: any) => ({
                                                    ...prev,
                                                    access: prev.access.filter((m: any) => m.value !== member.value)
                                                }))
                                            }
                                        />
                                    </Tag>
                                ))}
                            </Flex>
                        ))}
                </ModalBody>
                <ModalFooter gap={'33px'} padding={'0px'} marginTop={'24px'}>
                    <JButton
                        margin={'0px auto'}
                        size={'normal'}
                        variant={'j-primary'}
                        onClick={() => updateGroupMemberAccess.mutate()}
                        isLoading={updateGroupMemberAccess.isLoading}
                        isDisabled={groupMemberAccessDetail.isLoading}
                    >
                        {t('group_anggota_modal_berlakukan_simpan')}
                    </JButton>
                </ModalFooter>
            </ModalContent>
        </Modal>
    );
};

export default ModalApplyMemberGroup;
